import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Typography,
  message,
} from 'antd'
import {
  BankOutlined,
  ImportOutlined,
  InfoCircleOutlined,
  ApiOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import B2BLayout from '../../layouts/b2b'
import fetchAPI from '../../../lib/utils/fetch-api'
import { CompanyStructureV2Split } from './company/structure-v2-split'
import type { CompanyStructure, Office, Organization, RoleItem } from './company/types'
import { HomeImportSection } from './import-section'
import { HomeOnboardingSteps } from './onboarding-steps'
import type { OnboardingStatus } from './onboarding-steps'
import { selectIsTenantOwner, selectStartPage } from './owner-access'

const { Text, Paragraph, Title } = Typography

/**
 * Прежние блоки главной (Компания, Интеграции, Импорт, Полезное).
 * Временно скрыты: вместо них — пошаговый онбординг. Код сохранён — включить, поставив true.
 */
const SHOW_LEGACY_HOME_BLOCKS = false

/** /home — только для владельца кабинета; остальных отправляем на их стартовую страницу. */
export default function HomePage() {
  const isTenantOwner = useSelector(selectIsTenantOwner)
  const startPage = useSelector(selectStartPage)
  if (!isTenantOwner) return <Navigate to={startPage} replace />
  return <OwnerHomePage />
}

function OwnerHomePage() {
  const navigate = useNavigate()
  /** undefined — статус ещё грузится; null — имени нет */
  const [greetingName, setGreetingName] = useState<string | null | undefined>(undefined)
  const handleStatusLoaded = useCallback((status: OnboardingStatus | null) => {
    setGreetingName(status?.greeting_name?.trim() || null)
  }, [])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [structure, setStructure] = useState<CompanyStructure | null>(null)
  const [roles, setRoles] = useState<RoleItem[]>([])
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteTarget, setInviteTarget] = useState<{ org: Organization; office: Office } | null>(
    null,
  )
  const [form] = Form.useForm()

  const breadcrumbs = useMemo(() => [{ title: 'Главная' }], [])

  const loadStructure = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchAPI('/api/home/company-structure')
      if (res?.errors) {
        setError(res.errors?.[0]?.detail || 'Не удалось загрузить структуру компании')
        return
      }
      setStructure(res?.data || null)
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadRoles = useCallback(async () => {
    try {
      const res = await fetchAPI('/api/roles/list')
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []
      setRoles(
        list
          .filter((r: any) => r?.is_active !== false)
          .map((r: any) => ({
            id: Number(r.id),
            name: String(r.name || ''),
          })),
      )
    } catch {
      /* non-blocking */
    }
  }, [])

  useEffect(() => {
    loadStructure()
    loadRoles()
  }, [loadStructure, loadRoles])

  const openInvite = useCallback(
    (org: Organization, office: Office) => {
      setInviteTarget({ org, office })
      form.resetFields()
      setInviteOpen(true)
    },
    [form],
  )

  const submitInvite = async (values: any) => {
    if (!inviteTarget) return
    setInviteLoading(true)
    try {
      const res = await fetchAPI('/api/home/employees/invite', {
        method: 'POST',
        body: JSON.stringify({
          email: String(values.email || '').trim(),
          organization_id: inviteTarget.org.id,
          office_id: inviteTarget.office.id,
          role_ids: values.role_ids || [],
        }),
      })
      if (res?.errors) {
        message.error(res.errors?.[0]?.detail || 'Не удалось отправить приглашение')
        return
      }
      message.success(res?.data?.message || 'Приглашение отправлено')
      setInviteOpen(false)
      await loadStructure()
    } catch (e: any) {
      message.error(e?.message || 'Ошибка отправки')
    } finally {
      setInviteLoading(false)
    }
  }

  const hasAnyOrg =
    (structure?.brands?.some(b => b.organizations.length > 0) ||
      (structure?.organizations_without_brand?.length || 0) > 0) ??
    false

  return (
    <B2BLayout pageTitle="Главная" breadcrumbs={breadcrumbs}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <div style={{ width: '100%' }}>
          {greetingName !== undefined && (
            <Title level={3} style={{ marginTop: 0, marginBottom: 16 }}>
              {greetingName ? `Приветствуем, ${greetingName}!` : 'Приветствуем!'} Добро пожаловать
              в экосистему Ze-okna
            </Title>
          )}
          <Title level={4} style={{ margin: 0 }}>
            Настроим кабинет за несколько шагов
          </Title>
          <Text type="secondary">
            Идите по шагам сверху вниз. Любой шаг можно пропустить и вернуться к нему позже.
          </Text>
        </div>

        <HomeOnboardingSteps
          structure={structure}
          structureLoading={loading}
          structureError={error}
          onUsersImported={loadStructure}
          onStatusLoaded={handleStatusLoaded}
        />

        {SHOW_LEGACY_HOME_BLOCKS && (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card
              title={
                <Space>
                  <BankOutlined />
                  Компания
                </Space>
              }
              styles={{ body: { padding: 0 } }}
            >
              {loading && (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <Spin />
                </div>
              )}
              {error && <Alert type="error" showIcon message={error} />}
              {!loading && !error && !hasAnyOrg && (
                <Empty description="Организации ещё не созданы" />
              )}
              {!loading && !error && structure && hasAnyOrg && (
                <CompanyStructureV2Split structure={structure} onInvite={openInvite} />
              )}
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
              <Card
                title={
                  <Space>
                    <ApiOutlined />
                    Интеграции
                  </Space>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <div>
                    <Text strong>Телефония Mango</Text>
                    <br />
                    <Text type="secondary">Настраивается в карточке офиса. </Text>
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() => navigate('/new/organizations')}
                    >
                      К организациям
                    </Button>
                  </div>
                  <div>
                    <Text strong>Авито</Text>
                    <br />
                    <Text type="secondary">Подключение в настройках организации. </Text>
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() => navigate('/new/organizations')}
                    >
                      К организациям
                    </Button>
                  </div>
                  <div>
                    <Text strong>Calltouch</Text>
                    <br />
                    <Text type="secondary">Вебхуки и источники звонков.</Text>
                  </div>
                </Space>
              </Card>

              <Card
                title={
                  <Space>
                    <ImportOutlined />
                    Импорт данных
                  </Space>
                }
              >
                <HomeImportSection structure={structure} onUsersImported={loadStructure} />
              </Card>

              <Card
                title={
                  <Space>
                    <InfoCircleOutlined />
                    Полезное
                  </Space>
                }
              >
                <Space direction="vertical" size={4}>
                  <Text>Поддержка: напишите на почту платформы или менеджеру ZE.IQ.</Text>
                  <Link to="/admin/roles">Управление ролями</Link>
                  <Link to="/settings/modules">Модули и подписка</Link>
                </Space>
              </Card>
            </Space>
          </Col>
        </Row>
        )}
      </Space>

      <Modal
        title="Пригласить сотрудника"
        open={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={inviteLoading}
        okText="Отправить приглашение"
        destroyOnClose
      >
        {inviteTarget && (
          <Paragraph type="secondary">
            {inviteTarget.org.name} → {inviteTarget.office.name}
            {structure?.invite_ttl_hours
              ? `. Ссылка будет действительна ${structure.invite_ttl_hours} ч.`
              : ''}
          </Paragraph>
        )}
        <Form form={form} layout="vertical" onFinish={submitInvite}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Укажите email' },
              { type: 'email', message: 'Введите корректный email' },
            ]}
          >
            <Input placeholder="employee@example.ru" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="role_ids"
            label="Роли"
            rules={[
              {
                validator: (_, value) =>
                  value && value.length
                    ? Promise.resolve()
                    : Promise.reject(new Error('Выберите хотя бы одну роль')),
              },
            ]}
          >
            <Checkbox.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              options={roles.map(r => ({ label: r.name, value: r.id }))}
            />
          </Form.Item>
          <Button
            type="link"
            icon={<PlusOutlined />}
            style={{ padding: 0 }}
            onClick={() => navigate('/admin/roles')}
          >
            Управление ролями
          </Button>
        </Form>
      </Modal>
    </B2BLayout>
  )
}
